import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';
import ImageUpload from '../components/ImageUpload';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const TechnicalStaffForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    age: '',
    nationality: 'საქართველო',
    experience: '',
    specialization: '',
    description: '',
    photo_url: '',
    staff_type: 'medical',
    qualifications: []
  });
  
  const [newQualification, setNewQualification] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const staffTypes = [
    { value: 'medical', label: 'სამედიცინო პერსონალი' },
    { value: 'technical', label: 'ტექნიკური სპეციალისტები' }
  ];

  useEffect(() => {
    if (isEdit) {
      loadStaff();
    }
  }, [id, isEdit]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const response = await fetch(getApiUrl(`/api/technical-staff/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const member = await response.json();
        setFormData({
          name: member.name || '',
          position: member.position || '',
          age: member.age || '',
          nationality: member.nationality || 'საქართველო',
          experience: member.experience || '',
          specialization: member.specialization || '',
          description: member.description || '',
          photo_url: member.photo_url || '',
          staff_type: member.staff_type || 'medical',
          qualifications: member.qualifications || []
        });
      } else {
        console.error('Failed to load technical staff');
        navigate('/_panel-7c39f8/technical-staff');
      }
    } catch (error) {
      console.error('Error loading technical staff:', error);
      navigate('/_panel-7c39f8/technical-staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.position || !formData.staff_type) {
      alert('გთხოვთ შეავსოთ ყველა სავალდებულო ველი');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const url = isEdit ? `/api/technical-staff/${id}` : '/api/technical-staff/';
      const method = isEdit ? 'PUT' : 'POST';

      // Prepare data for submission
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/technical-staff');
      } else {
        const error = await response.json();
        alert(error.error || 'შეცდომა პერსონალის შენახვისას');
      }
    } catch (error) {
      console.error('Error saving technical staff:', error);
      alert('შეცდომა პერსონალის შენახვისას');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addQualification = () => {
    if (newQualification.trim() && !formData.qualifications.includes(newQualification.trim())) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, newQualification.trim()]
      }));
      setNewQualification('');
    }
  };

  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/_panel-7c39f8/technical-staff')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <WrenchScrewdriverIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  {isEdit ? 'პერსონალის რედაქტირება' : 'ახალი პერსონალი'}
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  {isEdit ? 'პერსონალის ინფორმაციის განახლება' : 'ახალი პერსონალის დამატება'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                ძირითადი ინფორმაცია
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სახელი და გვარი *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="პერსონალის სახელი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    პოზიცია *
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="პერსონალის პოზიცია"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ასაკი
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="80"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ასაკი წლებში"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ეროვნება
                  </label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ეროვნება"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    გამოცდილება
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="მაგ: 8 წელი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სპეციალიზაცია
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="სპეციალიზაცია"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    პერსონალის ტიპი *
                  </label>
                  <select
                    name="staff_type"
                    value={formData.staff_type}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    {staffTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <ImageUpload
                    value={formData.photo_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, photo_url: url }))}
                    category="photos"
                    label="ტექნიკური პერსონალის ფოტო"
                    required={false}
                    maxSize={50}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  აღწერა
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="პერსონალის აღწერა..."
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                კვალიფიკაციები
              </h3>
              
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newQualification}
                    onChange={(e) => setNewQualification(e.target.value)}
                    placeholder="ახალი კვალიფიკაცია..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                  />
                  <button
                    type="button"
                    onClick={addQualification}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    დამატება
                  </button>
                </div>
                
                {formData.qualifications.length > 0 && (
                  <div className="space-y-2">
                    {formData.qualifications.map((qualification, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                        <span className="text-sm text-gray-700 font-georgian">{qualification}</span>
                        <button
                          type="button"
                          onClick={() => removeQualification(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/_panel-7c39f8/technical-staff')}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium font-georgian"
            >
              გაუქმება
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian disabled:opacity-50"
            >
              {saving ? 'შენახვა...' : (isEdit ? 'განახლება' : 'დამატება')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default TechnicalStaffForm;
